import { isConditionGroup, type ConditionGroup } from '@reach/shared-types/conditions';
import type { SegmentConditionGroups } from '@reach/shared-types/conditions';

/**
 * AI-generated segment draft handed from the "Describe your segment" modal to
 * the create-segment page via React Router state (mirrored to sessionStorage so
 * a refresh preserves it). Consumed by DescribeSegmentModal and EditSegmentPage.
 */
export interface AiSegmentDraft {
  conditionGroups: SegmentConditionGroups;
  title?: string;
  description?: string;
  /**
   * Organization the draft was generated for. sessionStorage outlives logout and
   * organization switches, so the consumer must drop a draft owned by another.
   */
  organizationId: string;
}

/** sessionStorage mirror of the AI draft so a refresh mid-create keeps the seed. */
export const AI_SEGMENT_DRAFT_STORAGE_KEY = 'reach:ai-segment-draft';

export function clearAiSegmentDraft(): void {
  sessionStorage.removeItem(AI_SEGMENT_DRAFT_STORAGE_KEY);
}

/** A group counts only if it bottoms out in a real condition, at any nesting depth. */
function groupHasEffectiveCondition(group: ConditionGroup): boolean {
  return group.conditions.some(condition =>
    isConditionGroup(condition) ? groupHasEffectiveCondition(condition) : true,
  );
}

/**
 * The condition-groups schema accepts empty arrays at every level, so a
 * generation that built nothing still arrives as a well-formed success. Such a
 * draft cannot seed the builder and matches every contact once saved.
 */
export function isUsableAiSegmentDraft(
  conditionGroups: SegmentConditionGroups | undefined,
): conditionGroups is SegmentConditionGroups {
  return Boolean(conditionGroups?.some(groupHasEffectiveCondition));
}
